import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider.js';
import { SessionProvider } from './lib/session.js';
import Landing from './routes/landing/Landing.js';
import Privacy from './routes/landing/Privacy.js';
import NotFound from './routes/NotFound.js';
/**
 * Everything except the landing page loads on demand. PART F4 caps "/" at
 * 100KB, and a visitor reading the marketing page should not download the
 * game engine, the dashboard or the admin panel to get there.
 */
const JoinView = lazy(() => import('./routes/controller/JoinView.js'));
const DashboardLayout = lazy(() => import('./routes/dashboard/Layout.js'));
const Wizard = lazy(() => import('./routes/dashboard/Wizard.js'));
const Screens = lazy(() => import('./routes/dashboard/Screens.js'));
const MyGames = lazy(() => import('./routes/dashboard/MyGames.js'));
const Promotions = lazy(() => import('./routes/dashboard/Promotions.js'));
const Stats = lazy(() => import('./routes/dashboard/Stats.js'));
const Settings = lazy(() => import('./routes/dashboard/Settings.js'));
const LinkScreen = lazy(() => import('./routes/dashboard/Link.js'));
const AdminLayout = lazy(() => import('./routes/admin/Layout.js'));
const Overview = lazy(() => import('./routes/admin/Overview.js'));
const Venues = lazy(() => import('./routes/admin/Venues.js'));
const ScreensAll = lazy(() => import('./routes/admin/ScreensAll.js'));
const Runs = lazy(() => import('./routes/admin/Runs.js'));
const Games = lazy(() => import('./routes/admin/Games.js'));
const Login = lazy(() => import('./routes/auth/Login.js'));
const Register = lazy(() => import('./routes/auth/Register.js'));
/** Player surfaces wait on the dark background, never a white flash. */
const ScreenLazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div style={{ minHeight: '100dvh', background: 'var(--bg)' }} />}>
    {children}
  </Suspense>
);

/** Business surfaces render on their own background while a chunk arrives. */
const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div style={{ minHeight: '100dvh', background: 'var(--surface)' }} />}>
    {children}
  </Suspense>
);
/**
 * Phaser is ~1.5MB and only the TV ever needs it. Splitting it into its own
 * chunk keeps it off the landing page, which PART F4 caps at 100KB.
 */
const ScreenApp = lazy(() => import('./routes/screen/ScreenApp.js'));
/** The TV shows its own background while the game chunk arrives. */
const ScreenRoute = () => (
  <Suspense fallback={<div style={{ minHeight: '100dvh', background: 'var(--bg)' }} />}>
    <ScreenApp />
  </Suspense>
);
/** ThemeProvider needs router context, so it wraps inside the root route. */
function Root() {
  return (
    <ThemeProvider>
      <SessionProvider>
        <Outlet />
      </SessionProvider>
    </ThemeProvider>
  );
}
export const router = createBrowserRouter(
  [
    {
      element: <Root />,
      children: [
        { path: '/', element: <Landing /> },
        { path: '/privacy', element: <Privacy /> },
        {
          path: '/auth/login',
          element: (
            <Lazy>
              <Login />
            </Lazy>
          ),
        },
        {
          path: '/auth/register',
          element: (
            <Lazy>
              <Register />
            </Lazy>
          ),
        },
        // The TV tells the owner to go to lobbyfun.com/link (PART F1).
        {
          path: '/link',
          element: (
            <Lazy>
              <LinkScreen />
            </Lazy>
          ),
        },
        // Player surfaces (arcade theme).
        { path: '/screen', element: <ScreenRoute /> },
        {
          path: '/j/:screenCode',
          element: (
            <ScreenLazy>
              <JoinView />
            </ScreenLazy>
          ),
        },
        {
          path: '/dashboard',
          element: (
            <Lazy>
              <DashboardLayout />
            </Lazy>
          ),
          children: [
            {
              index: true,
              element: (
                <Lazy>
                  <Wizard />
                </Lazy>
              ),
            },
            {
              path: 'screens',
              element: (
                <Lazy>
                  <Screens />
                </Lazy>
              ),
            },
            {
              path: 'games',
              element: (
                <Lazy>
                  <MyGames />
                </Lazy>
              ),
            },
            {
              path: 'promotions',
              element: (
                <Lazy>
                  <Promotions />
                </Lazy>
              ),
            },
            {
              path: 'stats',
              element: (
                <Lazy>
                  <Stats />
                </Lazy>
              ),
            },
            {
              path: 'settings',
              element: (
                <Lazy>
                  <Settings />
                </Lazy>
              ),
            },
          ],
        },
        {
          path: '/admin',
          element: (
            <Lazy>
              <AdminLayout />
            </Lazy>
          ),
          children: [
            {
              index: true,
              element: (
                <Lazy>
                  <Overview />
                </Lazy>
              ),
            },
            {
              path: 'venues',
              element: (
                <Lazy>
                  <Venues />
                </Lazy>
              ),
            },
            {
              path: 'screens',
              element: (
                <Lazy>
                  <ScreensAll />
                </Lazy>
              ),
            },
            {
              path: 'runs',
              element: (
                <Lazy>
                  <Runs />
                </Lazy>
              ),
            },
            {
              path: 'games',
              element: (
                <Lazy>
                  <Games />
                </Lazy>
              ),
            },
          ],
        },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  // Opt in now so the TV console stays silent across a 48-hour unattended run.
  { future: { v7_relativeSplatPath: true } },
);
